import type { Dispatch, KeyboardEvent, RefObject, SetStateAction } from 'react'
import { useCallback, useState } from 'react'
import type { SelectorOption } from '../types'

interface UseSelectorKeyDownParams {
  open: boolean
  options: SelectorOption[]
  optionsRefs: RefObject<HTMLDivElement[]>
  selectedValue?: string
  setOpen: Dispatch<SetStateAction<boolean>>
}

export const useSelectorKeyDown = ({
  open,
  setOpen,
  options,
  optionsRefs,
  selectedValue,
}: UseSelectorKeyDownParams) => {
  const [focusedIndex, setFocusedIndex] = useState<number>(options.findIndex((option) => option.value === selectedValue))

  const focusOnSelectedOption = useCallback(() => {
    const currentIndex = options.findIndex((option) => option.value === selectedValue)
    setFocusedIndex(currentIndex)
  }, [options, selectedValue])

  const focusOnSelectedOrFirstOption = useCallback(() => {
    if (selectedValue) {
      focusOnSelectedOption()
    } else {
      setFocusedIndex(0)
    }
  }, [selectedValue, options])

  const selectFocusedOption = useCallback(() => {
    if (focusedIndex > -1) {
      optionsRefs.current && optionsRefs.current[focusedIndex]?.click()
    }
  }, [focusedIndex, optionsRefs])

  const handleArrowDownPlusAltKeyDown = useCallback(() => {
    if (!open) {
      setOpen(true)
      focusOnSelectedOrFirstOption()
    }
  }, [open, focusOnSelectedOrFirstOption])

  const handleArrowDownKeyDown = useCallback(() => {
    if (!open) {
      setOpen(true)
      focusOnSelectedOrFirstOption()
    } else {
      const nextIndex = focusedIndex === -1 ? 0 : Math.min(focusedIndex + 1, options.length - 1)
      setFocusedIndex(nextIndex)
    }
  }, [focusedIndex, focusOnSelectedOrFirstOption, open, options, selectedValue])

  const handleArrowUpPlusAltKeyDown = useCallback(() => {
    if (open) {
      selectFocusedOption()
      setOpen(false)
    }
  }, [open, selectFocusedOption])

  const handleArrowUpKeyDown = useCallback(() => {
    if (!open) {
      setOpen(true)
      if (selectedValue) {
        focusOnSelectedOption()
      } else {
        setFocusedIndex(options.length - 1)
      }
    } else {
      const prevIndex = focusedIndex === -1 ? options.length - 1 : Math.max(focusedIndex - 1, 0)
      setFocusedIndex(prevIndex)
    }
  }, [focusedIndex, open, options, selectedValue, setOpen])

  const handleEnterKeyDown = useCallback(() => {
    if (open) {
      selectFocusedOption()
    } else {
      setOpen(true)
    }
  }, [open, selectFocusedOption])

  const handleSpaceKeyDown = useCallback(() => {
    if (open) {
      selectFocusedOption()
    } else {
      setOpen(true)
    }
  }, [open, selectFocusedOption])

  const handleEscapeKeyDown = () => setOpen(false)

  const handleEndKeyDown = useCallback(() => {
    if (!open) {
      setOpen(true)
    }
    setFocusedIndex(options.length - 1)
  }, [open, options])

  const handleHomeKeyDown = useCallback(() => {
    if (!open) {
      setOpen(true)
    }
    setFocusedIndex(0)
  }, [open])

  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<Element>) => {
      switch (e.key) {
        case ' ':
          e.preventDefault()
          return handleSpaceKeyDown()
        case 'Enter':
          e.preventDefault()
          return handleEnterKeyDown()
        case 'Escape':
          return handleEscapeKeyDown()
        case 'ArrowDown':
          if (e.altKey) {
            return handleArrowDownPlusAltKeyDown()
          }
          return handleArrowDownKeyDown()
        case 'ArrowUp':
          if (e.altKey) {
            handleArrowUpPlusAltKeyDown()
          }
          return handleArrowUpKeyDown()
        case 'Home':
          return handleHomeKeyDown()
        case 'End':
          return handleEndKeyDown()
        default:
          return
      }
    },
    [
      handleArrowDownKeyDown,
      handleArrowDownPlusAltKeyDown,
      handleArrowUpKeyDown,
      handleArrowUpPlusAltKeyDown,
      handleEnterKeyDown,
      handleEscapeKeyDown,
      handleEndKeyDown,
      handleHomeKeyDown,
      handleSpaceKeyDown,
    ]
  )

  return { focusedIndex, handleTriggerKeyDown }
}
